import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {getAnalyticsV3ChannelsApi} from 'helpers/services/api/analytics';
import {unwrapAnalyticsPayload} from 'helpers/services/api/analyticsHelpers';

// Empty arrays mean "no filter" (all channels / all fulfillments).
const DEFAULT_FILTERS = {
  channelTypes: [],
  fulfillmentModes: [],
  availableChannelTypes: []
};

const AnalyticsFiltersContext = createContext(DEFAULT_FILTERS);
const AnalyticsFiltersUpdateContext = createContext({
  setChannelTypes: () => {},
  setFulfillmentModes: () => {}
});

export const useAnalyticsFilters = () => useContext(AnalyticsFiltersContext);

export const useAnalyticsFiltersUpdate = () =>
  useContext(AnalyticsFiltersUpdateContext);

export const AnalyticsFiltersProvider = ({children}) => {
  const locationId = useSelector((state) => state.user.locationId);
  const [channelTypes, setChannelTypes] = useState([]);
  const [fulfillmentModes, setFulfillmentModes] = useState([]);
  const [availableChannelTypes, setAvailableChannelTypes] = useState([]);

  useEffect(() => {
    setChannelTypes([]);
    setFulfillmentModes([]);
    setAvailableChannelTypes([]);

    if (!locationId) {
      return undefined;
    }

    let cancelled = false;

    const loadAvailableChannels = async () => {
      try {
        const response = await getAnalyticsV3ChannelsApi(locationId);
        const channels = unwrapAnalyticsPayload(response)?.channels;

        if (!cancelled) {
          setAvailableChannelTypes(Array.isArray(channels) ? channels : []);
        }
      } catch (error) {
        if (!cancelled) {
          setAvailableChannelTypes([]);
        }
      }
    };

    loadAvailableChannels();

    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const filters = useMemo(
    () => ({channelTypes, fulfillmentModes, availableChannelTypes}),
    [channelTypes, fulfillmentModes, availableChannelTypes]
  );

  const update = useMemo(() => ({setChannelTypes, setFulfillmentModes}), []);

  return (
    <AnalyticsFiltersContext.Provider value={filters}>
      <AnalyticsFiltersUpdateContext.Provider value={update}>
        {children}
      </AnalyticsFiltersUpdateContext.Provider>
    </AnalyticsFiltersContext.Provider>
  );
};

AnalyticsFiltersProvider.propTypes = {
  children: PropTypes.node.isRequired
};
